import { supabase } from './supabase';
import type { CoachingFormuleId } from '../config/coaching';
import type { Tarif } from './tarifsCoaching';

// PaymentIntent renvoyé par l'Edge Function coaching-payment (le montant est figé côté serveur).
export interface CoachingPaymentIntent {
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
  formule: CoachingFormuleId;
}

// Demande au serveur de créer le PaymentIntent pour la formule choisie.
// Le JWT de l'utilisateur connecté est attaché automatiquement par supabase.functions.invoke.
// Seule la formule part : le contact n'a rien à faire dans une demande de paiement, et le serveur
// n'en transmet aucun à Stripe (docs/coaching-paiement.md § 10).
export async function createCoachingPaymentIntent(formule: CoachingFormuleId): Promise<CoachingPaymentIntent> {
  const { data, error } = await supabase.functions.invoke<CoachingPaymentIntent>('coaching-payment', {
    body: { formule },
  });

  if (error) throw new Error('Préparation du paiement impossible. Réessaie.');
  if (!data?.client_secret) throw new Error('Préparation du paiement impossible. Réessaie.');
  return data;
}

/**
 * CATALOGUE DES TARIFS — LECTURE SEULE, MÊME FONCTION QUE LA CRÉATION.
 *
 * `method: 'GET'` distingue les deux usages. Ce n'est pas un détail de style : un corps malformé
 * peut produire un champ absent ou inattendu, il ne peut pas produire un `GET`. Côté serveur, la
 * réponse part AVANT toute lecture de corps et avant tout contact avec Stripe — donc aucun
 * PaymentIntent ne peut naître d'un affichage de prix.
 *
 * Le jeton de session part comme pour la création : `functions.invoke` résout l'`access_token` à
 * chaque appel. Rien à ajouter à la main.
 */
export interface TarifServeur {
  formule: CoachingFormuleId;
  amount: number;
  currency: string;
}

export async function fetchCatalogueTarifs(): Promise<Partial<Record<CoachingFormuleId, Tarif>>> {
  const { data, error } = await supabase.functions.invoke<{ tarifs: TarifServeur[] }>('coaching-payment', {
    method: 'GET',
  });
  if (error) throw new Error('Tarifs indisponibles.');

  const tarifs = data?.tarifs;
  // Une réponse qui n'a pas la forme attendue vaut une absence, jamais un montant par défaut.
  if (!Array.isArray(tarifs)) throw new Error('Tarifs indisponibles.');

  const catalogue: Partial<Record<CoachingFormuleId, Tarif>> = {};
  for (const t of tarifs) {
    // UNE ENTRÉE MALFORMÉE EST IGNORÉE, PAS CORRIGÉE. Un montant absent ou non numérique ne doit
    // pas devenir `0` : la formule concernée reste simplement indisponible.
    if (!t || typeof t.amount !== 'number' || typeof t.currency !== 'string') continue;
    catalogue[t.formule] = { amount: t.amount, currency: t.currency };
  }
  return catalogue;
}
